import { HttpEvent, HttpHandler, HttpResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService, Translation, TranslationObject } from '@ngx-translate/core';
import { Observable, of, tap } from 'rxjs';
import dayjs from 'dayjs';
import * as dayjsLocaleDe from 'dayjs/locale/de';
import * as dayjsLocalePl from 'dayjs/locale/pl';
import * as dayjsLocaleDa from 'dayjs/locale/da';

import * as LANGUAGE_DATA from './../Country_config.json';
import { IAppConfig } from './config/app-config.model';
import { APP_CONFIG } from './config/app-config.service';
import { XnStorageService } from './xn-storage';
import { ThemeService } from './theme/theme.service';


const CURRENT_LANGUAGE_STORAGE_KEY = 'current_language';
const BSL_THEME = 'BaseStack';
const XIOR_THEME = 'Xior';
const TRIBERAL_THEME = 'Triberal';

export interface Language {
  ID: number;
  Name: string;
  Culture_Code: string;
  UI_Culture_Code: string;
  enable?: boolean
}

const DEFAULT_LANGUAGE_CODE = 'en';

@Injectable({
  providedIn: 'root'
})
export class LanguageService {

  constructor(
    public translate: TranslateService,
    @Inject(APP_CONFIG) protected config: IAppConfig,
    private router: Router,
    public localStorageService: XnStorageService,
    private theme: ThemeService,
  ) { 
    this.loadLanguages();

    this.checkCurrentLanguageFromUrl();
  }

  locales = [dayjsLocaleDa, dayjsLocaleDe, dayjsLocalePl];

  languages: Language[] = [];
  currentLanguageCode = DEFAULT_LANGUAGE_CODE

  loadLanguages(){
    const availableLanguages = ((LANGUAGE_DATA as any).default as Language[]).filter(e => e.enable);
    this.languages = [...availableLanguages];

    if(this.config.availableLanguageCodes && this.config.availableLanguageCodes.length){
      const languages = availableLanguages.filter(e => this.config.availableLanguageCodes.includes(e.UI_Culture_Code));
      if(languages.length){
        this.languages = [...languages];
      }
    }
  }

  checkCurrentLanguageFromUrl(){
    const url = window.location.href;
    const reg = /lang=([a-z]{2})/i;
    if(!reg.test(url)) return;

    const match = reg.exec(url);
    const lang = match[1].toLowerCase();

    if(lang && this.isLanguageAvailable(lang)){
      this.localStorageService.setItem(CURRENT_LANGUAGE_STORAGE_KEY, lang);
    }
  }

  init(){
    let defaultLanguageCode = DEFAULT_LANGUAGE_CODE;

    const defaultLanguage = this.languages.find(e => e.UI_Culture_Code === defaultLanguageCode);
    if(!defaultLanguage && this.languages.length){
      // use the first language for default
      defaultLanguageCode = this.languages[0].UI_Culture_Code;
    }
    
    this.translate.addLangs(this.languages.map((e) => e.UI_Culture_Code));

    const currentLanguage = this.localStorageService.getItem(CURRENT_LANGUAGE_STORAGE_KEY);

    this.translate.setDefaultLang(defaultLanguageCode);
    this.currentLanguageCode = defaultLanguageCode;

    if(currentLanguage){
      const language = this.languages.find(e => e.UI_Culture_Code === currentLanguage);
      if(language){
        this.translate.use(currentLanguage);
        this.currentLanguageCode = currentLanguage;
        dayjs.locale(currentLanguage);
      }
    }
  }

  setLanguage(lang: string, disableReloadPage = false){
    this.currentLanguageCode = lang;
    this.localStorageService.setItem(CURRENT_LANGUAGE_STORAGE_KEY, lang);

    const currentURL = window.location.href;
    const langReg = /lang=([a-z]{2})/i;
    if(langReg.test(currentURL)){
      const match = langReg.exec(currentURL);
      if(match[1].toLowerCase() !== lang.toLowerCase()){
        let url = currentURL.replace(/lang=[a-z]{2}/i, '');

        this.translate.use(lang);
        dayjs.locale(lang);
        const path = url.replace(document.querySelector('base').href, '');
        this.router.navigateByUrl(path);
        return;
      }
    }

    if(!disableReloadPage && this.shouldReloadAfterLanguageChanged()){
      window.location.reload();
    }
    else{
      this.translate.use(lang);
      dayjs.locale(lang);
    }
  }

  getCurrentPath(pathName?: string){
    if(!pathName){
      pathName = window.location.pathname;
    }
    let path = pathName.replace(/^\//, '');
    if(this.config && this.config.webAppPathName && this.config.webAppPathName !== '/'){
      path = path.replace(this.config.webAppPathName.replace(/(^\/|\/$)/g, ''), '');
    }

    return path === '/' ? '' : path;
  }

  shouldReloadAfterLanguageChanged(){
    if(/(booking\/results|booking\/adons|booking\/submit|payment)/i.test(window.location.href)){
      return true;
    }
    const path = this.getCurrentPath();
    if(!path){
      // Homepage forece refresh
      return true;
    }

    return false;
  }

  getAvailableLanguages(): Language[]{
    return this.languages;
  }

  getCurrentLanguageCode(): string{
    return this.currentLanguageCode;
  }

  getCurrentLanguage(): Language{
    return this.languages.find(e => e.UI_Culture_Code === this.currentLanguageCode);
  }

  getCurrentCultureCode(){
    const language = this.languages.find(e => e.UI_Culture_Code === this.currentLanguageCode);
    return language ? language.Culture_Code : 'en-US';
  }

  turnOnCacheLanguagefile = false;
  handleRequest(req, next: HttpHandler) : Observable<HttpEvent<any>>{
    if(!this.turnOnCacheLanguagefile){
      return next.handle(req);
    }
    const url = req.url;
    const reg = /i18n\/([a-z]+)\.json/;
    let languageCode = '';
    if(reg.test(url)){
        languageCode = reg.exec(url)[1];
    }   

    let cacheKey = '';
    if(languageCode){
        cacheKey = `translate_data_${languageCode}`;
        if(sessionStorage.getItem(cacheKey)){
            try {
                const data = JSON.parse(sessionStorage.getItem(cacheKey));
                if(data){
                    const response = new HttpResponse({
                        url: req.url,
                        body: data
                    });

                    next.handle(req).subscribe((res) => {
                        const body = (res as any).body;

                        if(body && languageCode && cacheKey){
                            sessionStorage.setItem(cacheKey, JSON.stringify(body));
                        }
                    })
    
                    return of(response);
                }
            } catch (error) {
                
            }
        }
    }

    return next.handle(req).pipe(tap({
        next: (res) => {
            const body = (res as any).body;

            if(body && languageCode && cacheKey){
                sessionStorage.setItem(cacheKey, JSON.stringify(body));
            }
        }
    }))
  }

  getLanguageCount(): number{
    return this.languages.length;
  }

  isSelectableLanguage(){
    return this.getLanguageCount() > 0;
  }

  isLanguageAvailable(code: string){
    if(!code) return false;
    code = code.toLowerCase();
    const lang = this.languages.find(e => e.UI_Culture_Code.toLowerCase() === code);
    return lang ? true: false;
  }

  translateByTheme(key: string) {
    if(!key){
      console.log(`language ${key} is empty.`);
      return '';
    }
    
    const translateKey = this.getTranslateKeyByTheme(key);
    const result = this.translate.instant(translateKey);
    return result === translateKey ? this.translate.instant(key) : result;
  }

  getTranslateKeyByTheme(key: string): string {
    if(!key){
      return '';
    }

    let theme = this.config.themeDefault;
    let translateKey = key;

    if (theme === BSL_THEME) {
      translateKey = 'bsl.' + key;
    } else if (theme === XIOR_THEME) {
      translateKey = 'xior.' + key;
    } else if (theme === TRIBERAL_THEME) {
      translateKey = 'triberal.' + key;
    }

    return translateKey;
  }

  translateByThemeAsync(key: string): Observable<Translation | TranslationObject> {
    return new Observable((observer) => {
      const themeKey = this.getTranslateKeyByTheme(key);
      const params = {
        siteName: this.theme.getSiteName(),
        shortSiteName: this.theme.getShortSiteName(),
      };
      const keys: string[] = [key, themeKey];

      this.translate.get(keys, params).subscribe((texts) => {
        let value = themeKey;
        if(texts[themeKey] !== themeKey){
          value = texts[themeKey];
        } 
        else if(texts[key] !== key){
          value = texts[key];
        }

        observer.next(value);
      });
      
    });
    
  }


}
